/* Admin view-as (issue 2902) · first of all: isolates storage and fetch for a
   view-as tab before any other module runs. No-op in an ordinary tab. */
import { VIEW_AS_STATE } from './viewAsBoot';
/* iOS native shell network shim · MUST stay the first import after the view-as
   boot (it patches window.fetch before any other module can capture it) · no-op on web. */
import './native/nativeShellFetch';
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@territory/client/AuthContext';
import { keepaliveFetch } from './wedgeKeepaliveFetch';
/* Build target · VITE_WEDGE_TARGET=ios-lite ships only WedgeLiteRoute.
   Vite folds the check to a constant so the other import never emits. */
const LITE = import.meta.env.VITE_WEDGE_TARGET === 'ios-lite';
const WedgeApp = lazy(() => (LITE ? import('./WedgeLiteRoute') : import('./WedgeRootRoute')));
const WedgeNextApp = LITE ? null : lazy(() => import('./next/WedgeNextRoute'));
/* 1919 Forms (issue 4226): /forms/* renders inside the Wedge shell as the Forms
   tab (forms/FormsTabView), not as a separate route tree. */
import { WedgeAuthGate } from './WedgeAuthGate';
import { AuthCallbackWedge } from './AuthCallbackWedge';
import { IntegrationsCallbackWedge } from './IntegrationsCallbackWedge';
import { AccessDenied } from './AccessDenied';
import { WedgeGrant } from './WedgeGrant';
import { OfflineBanner } from './OfflineBanner';
import { WedgeUpdatePrompt } from './WedgeUpdatePrompt';
import { CopyCode } from './CopyCode';
import { ShareRedirect } from './ShareRedirect';
import { initWedgeAnalytics } from './wedgeAnalytics';
import { initWedgeRoute } from './wedgeRouteEngine';
import { BootSplashRelease } from './BootRadar';
import { ViewAsBanner, ViewAsFailedScreen } from './viewAs/ViewAsBanner';
import { DeviceFingerprintBeacon } from './wedgeDeviceFingerprint';
import { initNativeDeepLinks, installNativeLinkPolish, initNativePush, initNativeTextZoom, initNativeKeyboard, initNativeStatusBar, installNativeShareShim, installWedgeSessionGuards, initNativeBuildInfo } from './native/nativeBridge';
/* 2026-06-01 · WedgeResponsiveShell wrapper removed. The shell painted a fake
   iOS phone frame + tactical-HUD backdrop on viewports >= 768px, but the
   prototype (WedgeRoute -> WedgeRootApp) already drives its own desktop layout
   via useIsDesktop() (persistent SideNav + full-bleed center + right rail at
   >=768px, byte-identical phone at <768px). Wrapping it in the shell crammed
   the real app inside a narrow 9/19.5 phone box and pushed saved cards into a
   dark void. Mobile is unaffected because the shell only engaged at >=768px. */

/* 2026-05-31 · surface tag. wedge.repvantage.io is the PUBLIC surface (real
   users + YC reviewers only). The shared wedge bridge reads this to keep the
   internal tester tools (panel, reset suite, tier view-as) hidden here. Set
   before any wedge code mounts. See wedgeDataBridge.wedgeSurface(). */
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__WEDGE_SURFACE__ = 'public';
  (window as unknown as Record<string, unknown>).__WEDGE_TARGET__ = LITE ? 'ios-lite' : 'full';
  /* Build stamp · client-observable deploy verification. The deploy script
     bakes the git sha in via VITE_BUILD_STAMP so "deployed" can be confirmed
     from a rep-facing session (window.__WEDGE_BUILD__ or this console line),
     not just from SSH on the box. 'dev' means a local non-stamped build. */
  const buildStamp = import.meta.env.VITE_BUILD_STAMP || 'dev';
  (window as unknown as Record<string, unknown>).__WEDGE_BUILD__ = buildStamp;
  console.info(`[wedge] build ${buildStamp}`);
  /* 2026-07-28 · claim the boot trap installed by the inline script in
     index.html. Reaching this line means the module graph loaded and executed,
     so the trap's 12s "this build did not start" card must not paint.
     Anything it buffered before now (a resource 404 for a lazy chunk, an
     import-time throw that did not stop the boot) is real and would otherwise
     be lost, because WedgeRoute's reporter does not arm until React mounts. */
  const bootTrap = (window as unknown as { __WEDGE_BOOT__?: { booted: boolean; errors: unknown[] } }).__WEDGE_BOOT__;
  if (bootTrap) {
    bootTrap.booted = true;
    if (bootTrap.errors.length) console.warn('[wedge] errors before boot completed', bootTrap.errors);
  }
  /* Product analytics · installs window.__WEDGE_TRACK__ and idle-loads the
     PostHog SDK after first paint (never on the critical path). */
  if (VIEW_AS_STATE === 'off') initWedgeAnalytics();
  /* Route engine · installs window.__WEDGE_ROUTE__ (suggested-order sequencer
     + industry lexicon + Maps export) for the global-scope prototype to
     consume. Pure/offline; the UI stays dark behind WEDGE_MAPS. */
  initWedgeRoute();
  /* Siri App Intents route via repvantage://open?siri=... — attach the
     appUrlOpen listener as early as possible. No-op on web. */
  initNativeDeepLinks();
  /* Web-content links open in an in-app Safari sheet on native (maps, App
     Store, tel/mailto stay on the system path). No-op on web. */
  installNativeLinkPolish();
  /* APNs token refresh · NEVER prompts. Re-registers only when the rep
     already granted notification permission via the Settings toggle, because
     device tokens rotate across reinstalls/OS updates. No-op on web. */
  initNativePush();
  /* UX30 C12 · native share sheet shim at the navigator.share level (zero
     prototype call-site changes). No-op on web and until the app-local
     WedgeShare plugin ships on the ci lineage. */
  installNativeShareShim();
  /* UX30 C3 · keep-awake foreground guards + the wedge:keepawake event
     contract (disarm on background · battery landmine). No-op on web. */
  installWedgeSessionGuards();
  /* Dynamic Type · maps the OS preferred text size onto the webview once at
     boot (clamped 85-135%). No-op on web. */
  initNativeTextZoom();
  /* Keyboard polish · accessory-bar policy, --rv-kb-h CSS var, focused-field
     scroll assist (config resize mode is "none"). No-op on web. */
  initNativeKeyboard();
  /* Status bar · explicit style following the wedge theme + statusTap
     scroll-to-top for the inner screen scrollers. No-op on web. */
  initNativeStatusBar();
  /* Build identity for the Settings "What's new" row · the phone-side half of
     the anti-stale-build work. No-op on web (the row falls back to the web
     build stamp). */
  initNativeBuildInfo();
}

function LoginRoute() {
  const reason = new URLSearchParams(window.location.search).get('error');
  return <AccessDenied reason={reason} />;
}

// Silent push-SW registration · runs once on app boot. The SW is push-only
// (no fetch handler, no caching) so registering it is safe even before the
// rep has opted into notifications. Real subscription + permission prompt
// happens later when the rep flips the Notifications toggle in Settings.
// Skipped entirely when serviceWorker isn't available (older browsers /
// non-secure context). The SW file lives in apps/wedge/public/wedge-push-sw.js
// and is served at /wedge-push-sw.js via Vite's publicDir config.
if (VIEW_AS_STATE === 'off' && typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  /* 2026-06-10 (Background Resilience Wave 2) · register at parse-time
     (not window.load) so the SW + BroadcastChannel doorbell are live the
     moment the bundle loads. The 4-8s flaky-LTE delay between parse and
     load was identified by the prior boardroom as a real gap on iOS
     Safari · rep swiping out before load would never register the SW. */
  navigator.serviceWorker.register('/wedge-push-sw.js').catch((err) => {
    console.warn('[wedge] push SW registration failed:', err);
  });

  /* 2026-06-10 (Background Resilience Wave 2) · dynamic-import the push +
     A2HS helpers so a parse error in either file cannot crash boot. Both
     modules expose their public surface via window.__wedgePush and
     window.__WEDGE_A2HS__ so the passive amber chips in
     prep-screens-2.jsx can gate on them without static imports across
     the shell/prototype seam. Kill switches: VITE_WEDGE_PUSH_WAVE2_V1 +
     VITE_WEDGE_A2HS_V1 (read by the modules via window.__WEDGE_FLAGS__
     also set from index.html · default ENABLED · ops can disable by
     editing the build-time env or window.__WEDGE_FLAGS__ runtime). */
  /* b120 hygiene · the kill switch documented above had ZERO readers (a
     documented control that cannot control) · wired here. Default ENABLED;
     'false' via build env or a runtime __WEDGE_FLAGS__ false disables. */
  const pushWave2On = (() => {
    try {
      const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
      if (env && env.VITE_WEDGE_PUSH_WAVE2_V1 === 'false') return false;
      const f = (window as unknown as { __WEDGE_FLAGS__?: Record<string, unknown> }).__WEDGE_FLAGS__;
      if (f && f.VITE_WEDGE_PUSH_WAVE2_V1 === false) return false;
    } catch { /* default enabled */ }
    return true;
  })();
  if (pushWave2On) import('./wedgePush').then((mod) => {
    try {
      (window as unknown as Record<string, unknown>).__wedgePush = mod;
      void mod.ensureSwRegistered();
    } catch { /* assignment blocked · feature gracefully disabled */ }
  }).catch(() => undefined);
  import('./wedgeA2hs').then((mod) => {
    try {
      (window as unknown as Record<string, unknown>).__WEDGE_A2HS__ = mod;
      mod.installA2hsListeners();
    } catch { /* assignment blocked · noop */ }
  }).catch(() => undefined);

  /* 2026-06-04 · Apple Watch tap-to-view fix · SW -> page bridge.
     When the rep taps a Web Push notification (on iPhone OR on the
     Apple Watch mirror) and the wedge PWA is already open, the SW's
     notificationclick handler posts { type:'WEDGE_DEEP_LINK', url }
     to the focused client because iOS Safari does not support
     Client.navigate(). We translate that into a window CustomEvent the
     prototype's prep-app.jsx listens for, then it routes to the brief
     via bridge.prepareQuery (warm cache · no new credit). Also push
     the q+loc into the URL bar so a hot-reload preserves the deep-link
     and the existing briefDeepLinkRef path picks it up on next mount. */
  navigator.serviceWorker.addEventListener('message', (e) => {
    try {
      const data = (e && e.data) as { type?: string; url?: string } | undefined;
      if (!data || data.type !== 'WEDGE_DEEP_LINK' || !data.url) return;
      const u = new URL(data.url, window.location.origin);
      const q = (u.searchParams.get('q') || u.searchParams.get('account') || '').trim();
      const loc = (u.searchParams.get('loc') || u.searchParams.get('location') || '').trim();
      if (!q) return;
      try {
        const next = new URL(window.location.href);
        next.searchParams.set('q', q);
        if (loc) next.searchParams.set('loc', loc);
        window.history.replaceState({}, '', next.toString());
      } catch { /* ignore · the event still fires */ }
      window.dispatchEvent(new CustomEvent('wedge:deep-link', { detail: { q, loc } }));
    } catch (err) {
      console.warn('[wedge] SW deep-link message failed:', err);
    }
  });
}

/* 2026-05-29 Phase 26 B2 · Web Vitals dispatch to /api/wedge/web-vitals.
 * The backend endpoint exists in wedge-health.controller.ts (since 2026-05-26)
 * but no frontend dispatch was wired · live audit showed 0 samples in 30d.
 * Dynamically import web-vitals (already in apps/wedge/package.json) so it
 * code-splits and doesn't block the initial paint. Each metric (LCP / INP /
 * CLS / TTFB / FCP) fires its callback when the value is ready · we POST
 * { name, value, rating, path } shaped to the existing endpoint's strict
 * validator (name must be UPPERCASE 2-8 letters). credentials:'include'
 * is required so the rep's wedge session cookie attaches.
 * Fails open · vitals collection bugs must never crash the app.
 */
if (VIEW_AS_STATE === 'off' && typeof window !== 'undefined') {
  void (async () => {
    try {
      const wv = await import('web-vitals');
      const send = (metric: { name: string; value: number; rating?: string }) => {
        try {
          const body = JSON.stringify({
            name: metric.name,
            value: metric.value,
            rating: metric.rating ?? 'unknown',
            path: window.location.pathname || '/',
          });
          // sendBeacon survives unload races (LCP/CLS can fire late) · fall
          // back to fetch keepalive when sendBeacon isn't available.
          const url = '/api/wedge/web-vitals';
          if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            const blob = new Blob([body], { type: 'application/json' });
            navigator.sendBeacon(url, blob);
            return;
          }
          void keepaliveFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
          }).catch(() => { /* swallow · best-effort telemetry */ });
        } catch { /* ignore */ }
      };
      if (typeof wv.onLCP === 'function') wv.onLCP(send);
      if (typeof wv.onINP === 'function') wv.onINP(send);
      if (typeof wv.onCLS === 'function') wv.onCLS(send);
      if (typeof wv.onTTFB === 'function') wv.onTTFB(send);
      if (typeof wv.onFCP === 'function') wv.onFCP(send);
    } catch {
      // web-vitals not available or import failed · no telemetry, no error
    }
  })();

  /* Device fingerprint capture moved to DeviceFingerprintBeacon
     (wedgeDeviceFingerprint.ts), mounted inside WedgeAuthGate below: it only
     runs for a signed-in rep and goes through the authenticated client. */
}

/* iOS native shell · the launch splash auto-hides (launchAutoHide:true,
   800ms · build 14 dropped it from 4000ms because index.html now paints a
   branded boot skeleton instantly, so a long fixed splash just read as a
   slow app). This JS hide() is the happy-path early dismiss: double-rAF
   fires after React's first commit is on screen. NEVER flip launchAutoHide
   back to false · a hide path chained to window load hung the splash
   forever on build 3. Dynamic import + native gate = zero web effect. */
if (typeof window !== 'undefined' && (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.()) {
  const hideSplash = () => {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      import('@capacitor/splash-screen')
        .then(({ SplashScreen }) => SplashScreen.hide())
        .catch(() => { /* plugin absent · launchShowDuration fallback applies */ });
    }));
  };
  if (document.readyState === 'complete') hideSplash();
  else window.addEventListener('load', hideSplash, { once: true });
}

/* A view-as tab that could not be isolated shows a notice and mounts nothing else:
   the app would otherwise load the OWNER's own session in a tab that claims otherwise. */
if (VIEW_AS_STATE === 'failed') {
  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(<ViewAsFailedScreen />);
} else ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <BootSplashRelease />
    <BrowserRouter>
      <OfflineBanner />
      <WedgeUpdatePrompt />
      <AuthProvider>
        <ViewAsBanner />
        <Routes>
          <Route path="/auth/callback" element={<AuthCallbackWedge />} />
          <Route path="/integrations/callback" element={<IntegrationsCallbackWedge />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/grant" element={<WedgeGrant />} />
          <Route path="/copy" element={<CopyCode />} />
          <Route path="/share/text" element={<ShareRedirect via="text" />} />
          <Route path="/share/email" element={<ShareRedirect via="email" />} />
          {!LITE && <Route
            path="/next/*"
            element={
              <WedgeAuthGate>
                {VIEW_AS_STATE === 'off' && <DeviceFingerprintBeacon />}
                <Suspense fallback={null}><WedgeNextApp /></Suspense>
              </WedgeAuthGate>
            }
          />}
          <Route
            path="*"
            element={
              <WedgeAuthGate>
                {VIEW_AS_STATE === 'off' && <DeviceFingerprintBeacon />}
                <Suspense fallback={null}><WedgeApp /></Suspense>
              </WedgeAuthGate>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
